import React, { forwardRef, useImperativeHandle, useRef } from "react";

import { ActiveBox } from "../active-box";
import { BottomMenuPanel } from "../bottom-menu-panel";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../bottom-menu/buttons";
import { PlaceholderImage } from "../placeholder";

import "./designs-panel.css";

export interface IDesignsPanelItem {
	productCode: string;
	predefinedProductName: string;
	thumbUrl: string;
	absoluteDesignUrl: string;
}
export interface IDesignsPanelRef {
	scrollIntoView(name: string | undefined, animate?: boolean): void;
}

const DesignsThumb = ({
	thumbUrl,
	isActive,
	onClick,
}: {
	thumbUrl: string;
	isActive?: boolean;
	onClick?(): void;
}) => (
	<ActiveBox className="designs-panel__img-container" isActive={isActive}>
		<PlaceholderImage
			onClick={(e) => {
				e.stopPropagation();
				if (onClick) onClick();
			}}
			className="designs-panel__img"
			src={thumbUrl}
			loading="lazy"
		/>
	</ActiveBox>
);

interface IDesignsPanelProps {
	designs: readonly IDesignsPanelItem[];
	selectedPredefinedProductName?: string;
	onClick?(predefinedProductName: string): void;
}

export const DesignsPanel = forwardRef<IDesignsPanelRef, IDesignsPanelProps>(
	({ designs, selectedPredefinedProductName, onClick }, parentRef) => {
		const bottomMenuBarRef = useRef<IBottomMenuButtonsRef>(null);

		useImperativeHandle(
			parentRef,
			() => ({
				scrollIntoView: (name, animate = true) => {
					if (!bottomMenuBarRef.current) return;
					const index = designs.findIndex((d) => d.predefinedProductName === name);
					bottomMenuBarRef.current.scrollIntoView(index, {
						animate,
					});
				},
			}),
			[designs],
		);

		return (
			<BottomMenuPanel className="designs-panel">
				<BottomMenuButtons className="designs-panel__items" ref={bottomMenuBarRef}>
					{designs.map((design) => (
						<DesignsThumb
							key={design.predefinedProductName}
							thumbUrl={design.thumbUrl}
							isActive={
								selectedPredefinedProductName === design.predefinedProductName
							}
							onClick={() => {
								if (onClick) {
									onClick(design.predefinedProductName);
								}
							}}
						/>
					))}
				</BottomMenuButtons>
			</BottomMenuPanel>
		);
	},
);
DesignsPanel.displayName = "DesignsPanel";
